import { ThemedPageHeader, ThemedTextField } from 'common/styles';
import { wizardContextFromLaunchSource, WizardLaunchSource } from 'features/wizard/WizardContextBuilder';
import { connect } from 'react-redux';
import ContainerBase from 'common/containers/ContainerBase';
import ErrorMessage from 'common/components/ErrorMessage';
import FireflyAuthenticatedApi from 'api/FireflyAuthenticatedApi';
import GetUserTask from 'common/tasks/GetUserTask';
import GridContainer from 'common/components/GridContainer';
import GridItem from 'common/components/GridItem';
import { Intro } from 'features/wizard/styles';
import { linkAccounts } from 'actions';
import { mapStateToProps } from 'reducers';
import { NavButton } from 'common/components/nav-button';
import Page from 'common/components/Page';
import ProgressButton from 'common/components/ProgressButton';
import React from 'react';
import styled from 'theme';
import Validator from 'validation/Validator';
import { withTranslation } from 'react-i18next';

const WizardPage = styled(Page)`
    padding-top: 100px;
`;

const ButtonRow = styled.div`
    display: flex;
    justify-content: flex-end;
`;

class LinkAccountContainer extends ContainerBase {
    constructor(props) {
        super(props, {
            accountNumber: '',
            accountNumberError: null,
            accountPin: '',
            accountPinError: null,
            alias: '',
            aliasError: null
        });

        this.validator = new Validator(this);
    }

    render() {
        const { t } = this.props;

        const {
            accountNumber,
            accountNumberError,
            accountPin,
            accountPinError,
            alias,
            aliasError,
            loading
        } = this.state;

        return (
            <WizardPage padding="container">
                <GridContainer direction="column" alignItems="center">
                    <GridItem xs={12} sm={8} md={4}>
                        <ThemedPageHeader center={true}>{t('wizard.linkAccount.header')}</ThemedPageHeader>
                        <Intro>{t('wizard.linkAccount.intro')}</Intro>
                        <form autoComplete="off" onSubmit={this.linkAccount}>
                            <ThemedTextField
                                id="accountNumber"
                                inputRef={this.accountNumberFocus}
                                label={t('wizard.linkAccount.labelAccountNumber')}
                                type="number"
                                value={accountNumber}
                                disabled={loading}
                                onChange={this.handleInputChange('accountNumber')}
                                error={accountNumberError !== null}
                            />
                            <ErrorMessage
                                resourceId={accountNumberError}
                            />
                            <ThemedTextField
                                id="accountPin"
                                inputRef={this.accountPinFocus}
                                label={t('wizard.linkAccount.labelAccountPin')}
                                type="text"
                                value={accountPin}
                                disabled={loading}
                                onChange={this.handleInputChange('accountPin')}
                                error={accountPinError !== null}
                            />
                            <ErrorMessage
                                resourceId={accountPinError}
                            />
                            <ThemedTextField
                                id="alias"
                                inputRef={this.aliasFocus}
                                label={t('wizard.linkAccount.labelAlias')}
                                type="text"
                                value={alias}
                                disabled={loading}
                                onChange={this.handleInputChange('alias')}
                                error={aliasError !== null}
                            />
                            <ErrorMessage resourceId={aliasError} />
                            <ButtonRow>
                                <NavButton
                                    color="primary"
                                    disabled={loading}
                                    onClick={this.cancelWizard}
                                    spacingRight={true}
                                >
                                    {t('common.cancel')}
                                </NavButton>
                                <ProgressButton
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    loading={loading}
                                >
                                    {t('wizard.linkAccount.buttonContinue')}
                                </ProgressButton>
                            </ButtonRow>
                        </form>
                    </GridItem>
                </GridContainer>
            </WizardPage>
        );
    }

    handleInputChange = name => ({ target } = {}) => {
        this.setState({ [name]: target.value });
    };

    componentDidMount() {
        this.validator.maybeFocus();
    };

    componentDidUpdate() {
        this.validator.maybeFocus();
    };

    configureValidations(config) {
        config.requireNotEmpty('accountNumber');
        config.requireNotEmpty('accountPin');
    };

    cancelWizard = () => {
        const {
            history,
            location = this.props.location || {}
        } = this.props;
        if (location.state && location.state.launchSource === WizardLaunchSource.SETTINGS_ADD_ACCOUNT) {
            history.push('/settings/accounts');
        } else {
            history.push('/');
        }
    };

    linkAccount = e => {
        const {
            appState,
            dispatch,
            history,
            location = this.props.location || {}
        } = this.props;

        const {
            accountNumber,
            accountPin,
            alias
        } = this.state;

        e.preventDefault();

        if (!this.validator.validate()) {
            return;
        }

        this.setState({ loading: true });
        const api = new FireflyAuthenticatedApi({ appState, dispatch });
        api.linkAccount(
            accountNumber,
            accountPin,
            alias
        )
            .then(() => {
                const getUserTask = new GetUserTask(api);
                return getUserTask.execute();
            })
            .then(response => {
                this.setState({ loading: false });
                const wizardContext = wizardContextFromLaunchSource((location.state && location.state.launchSource));
                wizardContext.addAccount(
                    response.user,
                    response.accountDetails,
                    accountNumber
                );
                const firstStep = wizardContext.next(location);
                dispatch(
                    linkAccounts(
                        response.user,
                        response.accountDetails,
                        wizardContext
                    )
                );
                history.push(firstStep.path);
            })
            .catch(error => {
                this.setState({ loading: false });

                switch (error.status) {
                    case 422:
                        let firstError = null;
                        if (
                            error.response.errors &&
                            error.response.errors.length > 0 &&
                            error.response.errors[0].field === 'account_number'
                        ) {
                            firstError = error.response.errors[0].error_code;
                        } else if (error.response.error_code) {
                            firstError = error.response.error_code;
                        }

                        switch (firstError) {
                            case 'invalid':
                            case 'invalid_credentials':
                                this.showFieldError(
                                    'accountPin',
                                    'wizard.linkAccount.error.invalidAccountAndPin'
                                );
                                break;

                            case 'already_linked':
                                this.showFieldError(
                                    'accountNumber',
                                    'wizard.linkAccount.error.accountAlreadyLinked'
                                );
                                break;

                            default:
                                this.showErrorDialog(error);
                        }

                        break;

                    default:
                        this.showErrorDialog(error);
                }
            });
    }
}

export default connect(mapStateToProps)(withTranslation()(LinkAccountContainer));
